import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import useSEO from '@/lib/useSEO';
import { ArtworkImage } from '@/components/Artwork';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brush, Camera, Frame, Package, ShieldCheck, Printer } from 'lucide-react';

function StepCard({ n, title, body, Icon }) {
    return (
        <div className="relative rounded-2xl bg-[color:var(--paper-2)] border border-[color:var(--border)] p-5 sm:p-6 transition-shadow hover:shadow-[0_14px_40px_rgba(31,27,24,0.08)]">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <div className="w-9 h-9 rounded-full bg-[color:var(--paper)] border border-[color:var(--border)] flex items-center justify-center text-[color:var(--umber)]">
                    <Icon size={16} />
                </div>
                <span className="caption">Step {n}</span>
            </div>
            <h3 className="font-display text-lg sm:text-xl text-[color:var(--umber)]">{title}</h3>
            <p className="text-sm text-[color:var(--muted-fg)] mt-2 leading-relaxed">{body}</p>
        </div>
    );
}

export default function Home() {
    const [featured, setFeatured] = useState([]);
    useSEO({
        title: 'Bespoke animal portraits',
        description: 'Custom equine and dog portraits by Leiha Martin. Hand-drawn from your reference photograph, or ordered as a fine-art print.',
        path: '/',
    });
    useEffect(() => {
        api.get('/gallery').then((r) => {
            const feat = r.data.filter((x) => x.featured);
            setFeatured((feat.length ? feat : r.data).slice(0, 3));
        });
    }, []);

    return (
        <div>
            {/* Hero */}
            <section className="paper-wash noise relative overflow-hidden">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-14 items-center">
                        <div className="lg:col-span-5 fade-up order-2 lg:order-1">
                            <p className="caption mb-4 sm:mb-5" data-testid="hero-kicker">Bespoke animal portraits</p>
                            <h1 className="font-display text-[2.25rem] leading-[1.05] sm:text-5xl lg:text-6xl text-[color:var(--ink)] tracking-[-0.02em]">
                                Hand-drawn portraits of the animals you love.
                            </h1>
                            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[color:var(--muted-fg)] leading-relaxed max-w-md">
                                Custom equine and dog portraits by Leiha Martin. Thoughtful, detailed
                                artwork created from your chosen photograph.
                            </p>
                            <div className="mt-7 sm:mt-8 flex flex-wrap items-center gap-3">
                                <Link to="/commission" data-testid="hero-start-commission-button" className="w-full sm:w-auto">
                                    <Button className="w-full sm:w-auto rounded-xl bg-[color:var(--terra)] text-white hover:bg-[#B84F33] transition-colors px-5 py-6 text-[15px]">
                                        Commission a portrait
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </Link>
                                <Link to="/gallery" data-testid="hero-view-gallery-button" className="w-full sm:w-auto">
                                    <Button
                                        variant="outline"
                                        className="w-full sm:w-auto rounded-xl bg-[color:var(--paper-2)] border-[color:var(--border)] text-[color:var(--umber)] hover:bg-[color:var(--paper)] px-5 py-6 text-[15px]"
                                    >
                                        View gallery
                                    </Button>
                                </Link>
                            </div>
                            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-[color:var(--graphite)]">
                                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /><span>Secure UK checkout</span></div>
                                <div className="flex items-center gap-2"><Brush className="h-4 w-4" /><span>Individually drawn</span></div>
                                <div className="flex items-center gap-2"><Printer className="h-4 w-4" /><span>Print add-ons from £100</span></div>
                            </div>
                        </div>
                        <div className="lg:col-span-7 fade-up delay-200 order-1 lg:order-2">
                            {featured[0] && (
                                <div className="art-frame">
                                    <div className="art-frame-inner aspect-[4/5] sm:aspect-[16/12]">
                                        <ArtworkImage item={featured[0]} className="w-full h-full" loading="eager" />
                                    </div>
                                    {featured[0].title && (
                                        <div className="flex items-center justify-between px-2 pt-3 pb-1">
                                            <p className="font-display text-base text-[color:var(--umber)]">{featured[0].title}</p>
                                            <p className="text-xs text-[color:var(--muted-fg)]">{featured[0].medium}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="py-14 sm:py-20">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="caption mb-3">How it works</p>
                        <h2 className="font-display text-2xl sm:text-4xl text-[color:var(--umber)] leading-tight">
                            A quiet, considered process — from photograph to finished piece.
                        </h2>
                    </div>
                    <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StepCard n="1" title="Choose your size" body="Select from A5, A4, A3 or A2 — each suited to a different room and feel." Icon={Frame} />
                        <StepCard n="2" title="Upload your photo" body="Share a clear, well-lit reference image. Multiple photos welcome." Icon={Camera} />
                        <StepCard n="3" title="Pick framing & delivery" body="Unframed, standard or premium frame. UK or international delivery." Icon={Package} />
                        <StepCard n="4" title="Leiha creates your portrait" body="Each portrait is individually hand-drawn with careful attention to character." Icon={Brush} />
                    </div>
                </div>
            </section>

            {/* Prints add-on strip */}
            <section className="py-10 sm:py-14 border-t border-[color:var(--border)]">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                    <div className="md:col-span-2">
                        <p className="caption mb-2">Print add-ons</p>
                        <h3 className="font-display text-2xl sm:text-3xl text-[color:var(--umber)] leading-tight">Add archival fine-art prints to your commission.</h3>
                        <p className="text-sm sm:text-base text-[color:var(--muted-fg)] leading-relaxed mt-3 max-w-xl">Order prints of your finished portrait alongside the original — a thoughtful way to share it with family, or to hang in a second room. From £100.</p>
                    </div>
                    <div className="md:justify-self-end">
                        <Link to="/commission" data-testid="home-commission-cta-2">
                            <Button className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)] px-5 py-6">
                                Start a commission <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Featured */}
            <section className="py-14 sm:py-20 border-t border-[color:var(--border)]">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
                        <div>
                            <p className="caption mb-3">Selected work</p>
                            <h2 className="font-display text-2xl sm:text-4xl text-[color:var(--umber)] leading-tight">A small selection of recent portraits.</h2>
                        </div>
                        <Link to="/gallery" data-testid="home-view-all-link" className="under-link text-sm text-[color:var(--graphite)] hover:text-[color:var(--umber)] whitespace-nowrap">View gallery →</Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {featured.map((item, idx) => (
                            <div key={item.id} className={`art-frame group fade-up delay-${(idx + 1) * 100}`}>
                                <div className="art-frame-inner aspect-[4/5]">
                                    <ArtworkImage item={item} className="w-full h-full transition-transform duration-500 group-hover:scale-[1.02]" />
                                </div>
                                <div className="flex items-center justify-between px-2 pt-3 pb-1">
                                    <p className="font-display text-base text-[color:var(--umber)]">{item.title || 'Untitled'}</p>
                                    <p className="text-xs text-[color:var(--muted-fg)]">{item.medium}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Artist intro */}
            <section className="py-14 sm:py-20 border-t border-[color:var(--border)]">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                        <div className="lg:col-span-5">
                            <p className="caption mb-3">About</p>
                            <h2 className="font-display text-2xl sm:text-4xl text-[color:var(--umber)] leading-tight">A studio focused on the quiet detail of animals.</h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-base text-[color:var(--muted-fg)] leading-relaxed">
                                Each portrait is created individually from your chosen image, with careful attention to expression, character and detail. Leiha specialises in equines and dogs, working primarily in graphite and coloured pencil.
                            </p>
                            <p className="text-base text-[color:var(--muted-fg)] leading-relaxed mt-4">
                                Portraits make considered gifts — to mark a partnership, a friendship, or simply to live with a face you love a little longer.
                            </p>
                            <Link to="/commission" className="under-link mt-6 inline-block text-[color:var(--umber)]">Start a commission →</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
